import type { WindFieldPointForecast, WindGridBounds } from './windField';
import { SEVERE_WEATHER_CODES, weatherCodeMeta } from './weatherPoints';

export const EXC_TEMP_RANGE_C = 4;
export const EXC_RAIN_HI_PCT = 60;
export const EXC_RAIN_LO_PCT = 40;
export const EXC_SWELL_DELTA_M = 0.8;

export type ViewClassId = 'close' | 'medium' | 'wide';

export interface ViewClass {
  id: ViewClassId;
  spacingPx: number;
  maxArrows: number;
  glyphPx: number;
  showLabel: boolean;
}

const VIEW_CLASSES: Record<ViewClassId, ViewClass> = {
  // Swell grid density per zoom: at close zoom the sea fills the view so
  // nodes sit closer together; wide views carry more nodes overall (a
  // full-ocean grid replaces the old coastal line, Sep 2026).
  close: { id: 'close', spacingPx: 200, maxArrows: 60, glyphPx: 26, showLabel: true },
  medium: {
    id: 'medium',
    spacingPx: 140,
    maxArrows: 250,
    glyphPx: 22,
    showLabel: true,
  },
  // 3.63: wide views keep the full card (numbers included); they thin out
  // by spacing instead of abbreviating the card (Janna, 27 Oct).
  wide: { id: 'wide', spacingPx: 180, maxArrows: 400, glyphPx: 18, showLabel: true },
};

export function viewClassForSpan(spanKm: number | null): ViewClass {
  if (spanKm == null || spanKm < 25) return VIEW_CLASSES.close;
  if (spanKm < 120) return VIEW_CLASSES.medium;
  return VIEW_CLASSES.wide;
}

export interface LatLon {
  lat: number;
  lon: number;
}

function distanceSq(a: LatLon, b: LatLon): number {
  const toRad = Math.PI / 180;
  const lat1 = a.lat * toRad;
  const lat2 = b.lat * toRad;
  const dLat = (b.lat - a.lat) * toRad;
  const dLon = (b.lon - a.lon) * toRad;
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  return sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLon * sinLon;
}

export function nearestForecastPoint(
  points: WindFieldPointForecast[],
  anchor: LatLon,
): WindFieldPointForecast | null {
  let best: WindFieldPointForecast | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const point of points) {
    const distance = distanceSq(point, anchor);
    if (
      distance < bestDistance ||
      (distance === bestDistance &&
        (best == null || point.lat < best.lat || (point.lat === best.lat && point.lon < best.lon)))
    ) {
      best = point;
      bestDistance = distance;
    }
  }
  return best;
}

/** True when the point lies inside (or exactly on the edge of) the bounds. */
export function pointInsideBounds(point: LatLon, bounds: WindGridBounds): boolean {
  return (
    point.lat >= bounds.south &&
    point.lat <= bounds.north &&
    point.lon >= bounds.west &&
    point.lon <= bounds.east
  );
}

/** One Conditions point section derived from a loaded area cell. */
export interface AreaDerivedSection {
  data: WindFieldPointForecast | null;
  provenance: { lat: number; lon: number; label: string } | null;
}

/** The three Conditions point sections served from area cells. */
export interface AreaDerivedPointData {
  wind: AreaDerivedSection;
  weather: AreaDerivedSection;
  swell: AreaDerivedSection;
}

/**
 * v56 Task 6: derive the Conditions point sections from the nearest loaded
 * weather and marine area cells. Wind and Weather both come from the single
 * nearest weather cell (the same rule as the point path — one weather
 * response supplies both); Swell comes from the nearest marine cell.
 * Sections whose kind has no loaded cells are null.
 */
export function derivePointConditionsFromArea(input: {
  point: LatLon;
  weatherCells: WindFieldPointForecast[] | null;
  swellCells: WindFieldPointForecast[] | null;
}): AreaDerivedPointData {
  const { point, weatherCells, swellCells } = input;
  const weather =
    weatherCells && weatherCells.length > 0 ? nearestForecastPoint(weatherCells, point) : null;
  const swell =
    swellCells && swellCells.length > 0 ? nearestForecastPoint(swellCells, point) : null;
  return {
    wind: weather
      ? {
          data: weather,
          provenance: { lat: weather.lat, lon: weather.lon, label: 'nearest wind forecast cell' },
        }
      : { data: null, provenance: null },
    weather: weather
      ? {
          data: weather,
          provenance: {
            lat: weather.lat,
            lon: weather.lon,
            label: 'nearest weather forecast cell',
          },
        }
      : { data: null, provenance: null },
    swell: swell
      ? {
          data: swell,
          provenance: { lat: swell.lat, lon: swell.lon, label: 'nearest marine model cell' },
        }
      : { data: null, provenance: null },
  };
}

export type ViewportExceptionKind = 'severe-weather' | 'temperature' | 'rain' | 'swell';

export interface ViewportException {
  kind: ViewportExceptionKind;
  text: string;
}

function directionFrom(anchor: LatLon, point: LatLon): string {
  const dx = point.lon - anchor.lon;
  const dy = point.lat - anchor.lat;
  if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return 'nearby';
  const angle = (Math.atan2(dx, dy) * 180) / Math.PI;
  const index = Math.round(((angle + 360) % 360) / 45) % 8;
  return ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'][
    index
  ];
}

function hourAt(point: WindFieldPointForecast, hourIndex: number) {
  return point.hours[hourIndex] ?? null;
}

export function buildViewportExceptions(input: {
  anchor: LatLon;
  weather: WindFieldPointForecast[];
  swell: WindFieldPointForecast[];
  hourIndex: number;
}): ViewportException[] {
  const { anchor, weather, swell, hourIndex } = input;
  const result: ViewportException[] = [];
  const centreWeather = nearestForecastPoint(weather, anchor);
  const centreWeatherHour = centreWeather ? hourAt(centreWeather, hourIndex) : null;

  const severe = weather
    .filter((point) => point !== centreWeather)
    .map((point) => ({ point, hour: hourAt(point, hourIndex) }))
    .find(({ hour }) => hour?.weatherCode != null && SEVERE_WEATHER_CODES.has(hour.weatherCode));
  if (severe?.hour?.weatherCode != null) {
    const label = weatherCodeMeta(severe.hour.weatherCode).label.toLowerCase();
    result.push({
      kind: 'severe-weather',
      text: `${label} ${directionFrom(anchor, severe.point)}`,
    });
  }

  const temperatures = weather
    .map((point) => ({ point, value: hourAt(point, hourIndex)?.temperature }))
    .filter(
      (entry): entry is { point: WindFieldPointForecast; value: number } => entry.value != null,
    );
  if (temperatures.length > 1) {
    const low = temperatures.reduce((a, b) => (b.value < a.value ? b : a));
    const high = temperatures.reduce((a, b) => (b.value > a.value ? b : a));
    if (high.value - low.value >= EXC_TEMP_RANGE_C) {
      const centre = centreWeatherHour?.temperature;
      const useHigh = centre == null || high.value - centre >= centre - low.value;
      const chosen = useHigh ? high : low;
      result.push({
        kind: 'temperature',
        text: `${useHigh ? 'warmer' : 'cooler'} ${directionFrom(anchor, chosen.point)}`,
      });
    }
  }

  const centreRain = centreWeatherHour?.precipitationProbability;
  const rainy = weather
    .map((point) => ({ point, value: hourAt(point, hourIndex)?.precipitationProbability }))
    .filter(
      (entry): entry is { point: WindFieldPointForecast; value: number } => entry.value != null,
    )
    .reduce<{ point: WindFieldPointForecast; value: number } | null>(
      (best, entry) => (best == null || entry.value > best.value ? entry : best),
      null,
    );
  if (
    rainy &&
    rainy.value >= EXC_RAIN_HI_PCT &&
    centreRain != null &&
    centreRain < EXC_RAIN_LO_PCT
  ) {
    result.push({ kind: 'rain', text: `rain ${directionFrom(anchor, rainy.point)}` });
  }

  const centreSwell = nearestForecastPoint(
    swell.filter((point) => hourAt(point, hourIndex)?.swellHeight != null),
    anchor,
  );
  const centreHeight = centreSwell ? hourAt(centreSwell, hourIndex)?.swellHeight : null;
  const strongest = swell
    .map((point) => ({ point, value: hourAt(point, hourIndex)?.swellHeight }))
    .filter(
      (entry): entry is { point: WindFieldPointForecast; value: number } => entry.value != null,
    )
    .reduce<{ point: WindFieldPointForecast; value: number } | null>(
      (best, entry) => (best == null || entry.value > best.value ? entry : best),
      null,
    );
  if (strongest && centreHeight != null && strongest.value - centreHeight >= EXC_SWELL_DELTA_M) {
    result.push({ kind: 'swell', text: `bigger swell ${directionFrom(anchor, strongest.point)}` });
  }

  return result.slice(0, 2);
}
